import { Route, Routes } from 'react-router';
import { KycPage } from './page';
import { KycAccessGuard } from './guard';
import { KycLayout } from './layout';
import { KycContextProvider, type KycProviderProps } from './context';
import { KycSuspenseBoundary } from './kyc-suspense-boundary';

type KycRouterProps = KycProviderProps;

/**
 * Self-contained KYC verification flow.
 * Wraps context, error boundaries, suspense, and routing into a single component.
 *
 * Internally renders `KycLayout → KycAccessGuard → KycPage`, which selects the appropriate provider
 * (Fourthline or IDnow) based on the user's KYC status. If KYC is already completed,
 * the guard calls `onComplete` immediately.
 *
 * @see {@link KycContextValue} for prop documentation.
 *
 * @example
 * ```tsx
 * <KycRouter
 *   legalCountry={company.legalCountry}
 *   hasGermanResidencePermit={false}
 *   onComplete={handleComplete}
 *   trackingPrefix="verification"
 *   membershipId={membership.id}   // optional, for tracking
 *   userId={membership.userId}      // optional, for tracking
 * />
 * ```
 */
export function KycRouter(contextProps: KycRouterProps) {
  return (
    <KycContextProvider {...contextProps}>
      <KycSuspenseBoundary>
        <Routes>
          <Route element={<KycLayout />}>
            <Route element={<KycAccessGuard />}>
              <Route element={<KycPage />} index />
            </Route>
          </Route>
        </Routes>
      </KycSuspenseBoundary>
    </KycContextProvider>
  );
}
